/**
 * 🧱 Refactor BT Phase 3 — T1 : `useBtEditerSauvegarde` — code déplacé TEL QUEL depuis EditerBonTravailDialog.jsx
 * (ex-L780-928, avant T1) : `showConfirmConge`, `autoSaveBtFn`, `useAutoSave` (indicateur auto-save), `handleSave`, `doSave` (PUT via `construirePayloadEditer`).
 * `ctx` = noms du composant lus par ce code (mêmes valeurs de rendu qu'avant) ; retourne ce qu'il déclare.
 */
import { API_URL } from '../components/crm/bon-travail/utils';
import axios from 'axios';
import { construirePayloadEditer } from '../lib/btPayload';
import { persistPlanificationApprox } from '../components/crm/bon-travail/PlanificationApproxSection';
import { toast } from 'sonner';
import useAutoSave from './useAutoSave';
import { useState } from 'react';

export function useBtEditerSauvegarde(ctx) {
  const {
    bon,
    employees,
    employesOriginal,
    extrasRetiresManuellement,
    form,
    initialPlanifApproxRef,
    isExt,
    leaveConflicts,
    lockedInfo,
    membresExclus,
    membresExtras,
    onSaved,
    originalAssignesRef,
    planificationApprox,
    saving,
    setSaving,
  } = ctx;

  const [showConfirmConge, setShowConfirmConge] = useState(false);

  // 🆕 [Phase 43 BIS] Auto-save BT — uniquement en mode édition (BT existant),
  // pas en création. Debounce 3 s. Échec silencieux (rethrow → status='error'
  // sans toast). Le bouton « Enregistrer » manuel reste indépendant.
  const autoSaveBtFn = async () => {
    if (!bon?.id) return;
    if (!form.description_travaux?.trim()) return; // skip si validation client échoue
    if (saving) return; // skip si save manuel en cours
    await doSave({ silent: true });
  };
  const { status: autoSaveStatus, lastSavedAt } = useAutoSave(
    form,
    autoSaveBtFn,
    { debounceMs: 3000, enabled: !!bon?.id }
  );

  const handleSave = async () => {
    if (!form.description_travaux.trim()) { toast.error('Description requise'); return; }
    // Double-validation si conflit de congé détecté (state `leaveConflicts` alimenté plus haut)
    if (leaveConflicts.length > 0 && !showConfirmConge) {
      setShowConfirmConge(true);
      return;
    }
    setShowConfirmConge(false);
    await doSave();
  };

  const doSave = async ({ silent = false } = {}) => {
    setSaving(true);
    try {
      // 🧱 Refactor BT Phase 3 — construction du payload déplacée telle quelle dans lib/btPayload.js
      const putPayload = construirePayloadEditer({
        bon, form, employees, employesOriginal, membresExtras, membresExclus,
        lockedInfo, originalAssignesRef, extrasRetiresManuellement, isExt,
      });
      const res = await axios.put(`${API_URL}/api/crm/bons-travail/${bon.id}`, putPayload);
      if (res.status !== 200) {
        const errData = res.data || {};  // 🆕 Phase E2 — fix copy-paste axios (data déjà parsé)
        console.error('Erreur save bon:', res.status, errData);
        throw new Error(errData.detail || 'Erreur');
      }
      // ✅ PHASE 2 Location : afficher les conflits détectés après recalcul
      const conflits = res.data?.conflits_detectes || [];
      // 🆕 2026-05-06 Module CCQ Phase 6.2 — avertissement non bloquant si grade manquant
      const ccqWarnings = res.data?.ccq_warnings || [];
      if (conflits.length > 0) {
        const resume = conflits.slice(0, 3).map(c =>
          `• ${c.equipement_nom} le ${c.date} (${c.conflit_avec})`
        ).join('\n');
        const extra = conflits.length > 3 ? `\n… et ${conflits.length - 3} autre(s)` : '';
        if (!silent) toast.warning(
          `⚠️ ${conflits.length} conflit(s) d'équipement(s) après déplacement des dates :\n${resume}${extra}`,
          { duration: 10000 }
        );
      } else {
        if (!silent) toast.success('Bon de travail modifié!');
      }
      // 🆕 2026-05-08 — Alerte si le BT inclut des jours de weekend
      const weekendDates = (form.dates_chantier || []).filter(d => {
        const day = new Date(d + 'T12:00:00').getDay();
        return day === 0 || day === 6;
      });
      if (weekendDates.length > 0) {
        const labels = weekendDates.slice(0, 5).map(d => {
          const dt = new Date(d + 'T12:00:00');
          const jour = dt.getDay() === 0 ? 'Dim.' : 'Sam.';
          return `${jour} ${d}`;
        }).join(', ');
        const extra = weekendDates.length > 5 ? `, … (+${weekendDates.length - 5})` : '';
        toast.warning(
          `🟡 Ce BT est cédulé sur ${weekendDates.length} jour(s) de weekend : ${labels}${extra}. Vérifiez les taux temps double / prime CCQ avant la paie.`,
          { duration: 10000 }
        );
      }
      // CCQ : avertir si le BT est CCQ mais que des employés n'ont pas de grade
      if (ccqWarnings.length > 0) {
        const noms = ccqWarnings.slice(0, 5).map(w => `• ${w.nom_complet}`).join('\n');
        const extra = ccqWarnings.length > 5 ? `\n… et ${ccqWarnings.length - 5} autre(s)` : '';
        toast.warning(
          `🏛️ BT en régime CCQ — ${ccqWarnings.length} employé(s) sans grade défini :\n${noms}${extra}\n\nCorrigez dans Gestion des Employés → onglet Salaires.`,
          { duration: 12000 }
        );
      }
      // 🐛 [Phase v13.48 — 2026-07-14] Persistance de la planification approximative
      //   (silent-fail : la sauvegarde principale du BT ne doit pas être annulée
      //   par un souci de planif approx).
      try {
        await persistPlanificationApprox(bon.id, planificationApprox, initialPlanifApproxRef.current);
        initialPlanifApproxRef.current = planificationApprox;
      } catch (errPlanif) {
        console.error('Erreur persist planification approximative:', errPlanif);
        if (!silent) toast.warning('Bon sauvegardé mais la planification approximative n\'a pas été mise à jour.');
      }
      if (!silent) onSaved();
    } catch (e) {
      // 🆕 [Phase 43 BIS] En mode silent (auto-save), on rethrow pour que useAutoSave
      // marque le status='error' sans afficher de toast — l'utilisatrice continue
      // de travailler sans interruption.
      if (silent) {
        console.warn('[autosave-BT] échec silencieux:', e?.response?.status, e?.message);
        throw e;
      }
      // 🐛 2026-05-19 — Avant : on affichait toujours « Erreur lors de la modification »
      //   ce qui masquait la cause réelle (ex: 409 DATES_VERROUILLEES, employé verrouillé,
      //   etc.). Maintenant on remonte le `detail` renvoyé par le backend FastAPI,
      //   avec un toast richement formaté (titre + description multi-lignes).
      console.error('Erreur sauvegarde BT:', e);
      const respData = e?.response?.data;
      let title = 'Erreur lors de la modification';
      let description = null;

      if (respData) {
        const d = respData.detail ?? respData;
        if (typeof d === 'string') {
          title = d;
        } else if (Array.isArray(d)) {
          // FastAPI 422 (validation Pydantic)
          title = 'Données invalides';
          description = d.slice(0, 3).map(err => {
            const field = Array.isArray(err.loc) ? err.loc.slice(1).join('.') : err.loc;
            return `• ${field || 'champ'} : ${err.msg || 'invalide'}`;
          }).join('\n');
          if (d.length > 3) description += `\n… (+${d.length - 3} autre(s))`;
        } else if (d && typeof d === 'object') {
          title = d.message || d.msg || d.error || title;
          const parts = [];
          if (Array.isArray(d.dates) && d.dates.length > 0) {
            const aper = d.dates.slice(0, 5).join(', ');
            const extra = d.dates.length > 5 ? `, … (+${d.dates.length - 5})` : '';
            parts.push(`📅 Dates : ${aper}${extra}`);
          }
          if (d.employee_nom) parts.push(`👤 ${d.employee_nom}`);
          if (parts.length) description = parts.join('\n');
        }
      } else if (e?.message) {
        title = e.message;
      }

      toast.error(title, {
        description,
        duration: 15000,
        // Sonner supporte multi-ligne via la prop description (pre-wrap natif)
      });
    }
    finally { setSaving(false); }
  };

  // Calendrier

  return { autoSaveBtFn, autoSaveStatus, doSave, handleSave, lastSavedAt, setShowConfirmConge, showConfirmConge };
}
